import { useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { AiKeyMissingNotice } from './AiKeyBanner.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import { Modal } from './ui';
import AiBusy, { AI_COST } from './AiBusy.jsx';
import { formatDateTime } from '../util/time.js';
import { lazyImport } from '../util/lazyImport.js';

// T031 — Per-recommendation interview prep pack.
//
// Five STAR-formatted question drafts grounded in the candidate's actual
// profile + the job's actual description. Operator opens this from the
// Kanban side drawer when a card hits screening/interview; the prep
// pack lets them rehearse with material they could actually use.
//
// Generate / Regenerate / Discard / Copy. Same shape as the cover letter
// modal so the operator's mental model is consistent.

const KIND_LABEL = {
  behavioural: 'Behavioural',
  technical: 'Technical',
  situational: 'Situational',
  motivation: 'Motivation',
};

const KIND_TONE = {
  behavioural: 'border-brand-700 text-brand-400 bg-brand-600/10',
  technical: 'border-emerald-700 text-emerald-300 bg-emerald-950/20',
  situational: 'border-amber-700 text-amber-300 bg-amber-950/20',
  motivation: 'border-slate-700 text-slate-300 bg-slate-900/40',
};

export default function InterviewPrepModal({ rec, onClose, onUpdated }) {
  const job = rec?.job;
  const existing = rec?.interviewPrep ?? null;
  const { user } = useAuth();
  const hasAnthropicKey = !!user?.connectors?.anthropicKey;
  const [prep, setPrep] = useState(existing);
  const [generating, setGenerating] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Escape is owned by <Modal> (suppressed via escapeClosesOnTop while the
  // nested discard ConfirmDialog is open).

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/interview-prep`, {});
      const next = r.recommendation.interviewPrep;
      setPrep(next);
      toast(`Generated ${next.questions.length} prep questions`, { kind: 'success' });
      onUpdated?.({ interviewPrep: next });
    } catch (e) {
      const msg =
        e.body?.error === 'anthropic_unauthorized'
          ? 'Set your Anthropic API key in Settings → Connectors first.'
          : e.body?.message || e.message;
      toast(msg, { kind: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const discard = async () => {
    if (!prep) return;
    try {
      await api.delete(`/recommendations/${rec.id}/interview-prep`);
      setPrep(null);
      toast('Prep discarded', { kind: 'success' });
      onUpdated?.({ interviewPrep: null });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    }
  };

  // T448 — the prep pack as a PDF, for reading away from the screen.
  //
  // He asked for this after the Autoflows interview, where the useful
  // artefact was a document he could read on the way there — and I made
  // that one by hand, outside the product, while this modal held the same
  // material and could only put it on the clipboard.
  //
  // Same machinery as the cover letter's export (T025/T339): jsPDF through
  // `lazyImport`, so a tab older than the last deploy is told to reload
  // instead of 404-ing on a chunk filename. Laid out for reading rather
  // than for sending: one question per block, the STAR beats labelled and
  // indented, and a page break never splits a label from its text.
  const downloadPdf = async () => {
    if (!prep?.questions?.length) return;
    try {
      const { jsPDF } = await lazyImport(() => import('jspdf'), 'The PDF exporter');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 56;
      const maxW = pageW - margin * 2;
      let y = margin;

      const room = (needed) => {
        if (y + needed > pageH - margin) {
          doc.addPage();
          y = margin;
          return true;
        }
        return false;
      };

      // Block of wrapped text that will not be orphaned from its label:
      // the label and its first line are measured together, so a break
      // lands before the pair rather than between them.
      const block = (label, text, { labelFont = 'bold', size = 10.5, gap = 4 } = {}) => {
        if (!text) return;
        doc.setFontSize(size);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(text), maxW - 54);
        room(15 + 14);
        doc.setFont('helvetica', labelFont);
        doc.setTextColor(90);
        doc.text(label, margin, y);
        doc.setTextColor(20);
        doc.setFont('helvetica', 'normal');
        lines.forEach((line, i) => {
          if (i > 0) room(14);
          doc.text(line, margin + 54, y);
          y += 14;
        });
        y += gap;
      };

      // Header — who this is for, so a printed page is not anonymous.
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      const titleLines = doc.splitTextToSize(job?.title ?? 'Interview prep', maxW);
      doc.text(titleLines, margin, y);
      y += titleLines.length * 20;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(110);
      const meta = [job?.company, [job?.location?.city, job?.location?.country].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join('   ·   ');
      if (meta) {
        doc.text(meta, margin, y);
        y += 16;
      }
      doc.setFontSize(9.5);
      doc.text(`Interview prep · ${prep.questions.length} questions`, margin, y);
      doc.setTextColor(20);
      y += 10;
      doc.setDrawColor(205);
      doc.line(margin, y, pageW - margin, y);
      y += 22;

      prep.questions.forEach((q, i) => {
        // Keep the question with at least its first STAR label.
        room(60);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        const qLines = doc.splitTextToSize(`${i + 1}. ${q.question}`, maxW);
        qLines.forEach((line, li) => {
          if (li > 0) room(16);
          doc.text(line, margin, y);
          y += 16;
        });
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(KIND_LABEL[q.kind] ?? q.kind, margin, y);
        doc.setTextColor(20);
        y += 14;

        block('Situation', q.star?.situation);
        block('Task', q.star?.task);
        block('Action', q.star?.action);
        block('Result', q.star?.result);
        if (q.tip) block('Tip', q.tip, { labelFont: 'italic' });

        y += 10;
      });

      const safe = (v) =>
        String(v ?? '')
          .replace(/[\\/:*?"<>|\x00-\x1f]/g, '')
          .trim()
          .slice(0, 100);
      const filename =
        ['Interview Prep', safe(job?.company), safe(job?.title)].filter(Boolean).join(' - ') + '.pdf';
      doc.save(filename);
      toast('PDF downloaded', { kind: 'success' });
    } catch (e) {
      // A stale build is not an export failure — nothing is wrong with the
      // prep, and the only useful thing to say carries a button.
      if (e?.stale) {
        toast(e.message, { kind: 'error', action: { label: 'Reload', onClick: () => window.location.reload() } });
        return;
      }
      toast(`PDF export failed: ${e.message}`, { kind: 'error' });
    }
  };

  const copyAll = async () => {
    if (!prep) return;
    const text = prep.questions
      .map(
        (q, i) =>
          `Q${i + 1} (${KIND_LABEL[q.kind] ?? q.kind}): ${q.question}\n\n` +
          `Situation: ${q.star.situation}\n` +
          `Task: ${q.star.task}\n` +
          `Action: ${q.star.action}\n` +
          `Result: ${q.star.result}\n` +
          (q.tip ? `Tip: ${q.tip}\n` : ''),
      )
      .join('\n───\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast('All questions copied', { kind: 'success' });
    } catch {
      toast('Copy failed — select + Cmd-C from the visible text', { kind: 'info' });
    }
  };

  return (
    <>
    {/* T131-fu — <Modal> (bare, size lg, scroll); the nested discard
        ConfirmDialog is a SIBLING below, owning Escape while it's open. */}
    <Modal
      role="dialog"
      size="lg"
      scroll
      labelledById="interview-prep-title"
      onClose={onClose}
      escapeClosesOnTop={!confirmDiscard}
    >
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">Interview prep</p>
            <h2 id="interview-prep-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">
              {job?.title ?? 'Job'}
              <span className="ml-2 font-normal text-slate-400">@ {job?.company ?? '?'}</span>
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              5 likely questions with STAR drafts grounded in your profile.
              {prep && (
                <>
                  {' · '}generation {prep.generationCount}
                  {prep.generatedAt && ` · ${formatDateTime(prep.generatedAt)}`}
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {!prep ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Generate a prep pack for <strong className="text-slate-200">{job?.title}</strong> at{' '}
                <strong className="text-slate-200">{job?.company}</strong>. Mix of behavioural + technical questions,
                each with a STAR draft you can rehearse. Cost: {AI_COST.interviewPrep.cost} per generation.
              </p>
              {hasAnthropicKey ? (
                <button
                  type="button"
                  onClick={generate}
                  disabled={generating}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-500 disabled:opacity-50"
                >
                  {generating ? <AiBusy feature="interviewPrep" compact /> : '✨ Generate prep pack'}
                </button>
              ) : (
                <AiKeyMissingNotice feature="Interview prep" />
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {prep.questions.map((q, i) => (
                <QuestionCard key={i} index={i + 1} question={q} />
              ))}
            </div>
          )}
        </div>

        {prep && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
            {hasAnthropicKey && (
              <button
                type="button"
                onClick={generate}
                disabled={generating}
                className="rounded-md border border-brand-600 px-3 py-1.5 text-xs text-brand-500 hover:bg-brand-600/10 disabled:opacity-50"
                title="Re-run — overwrites the current pack"
              >
                {generating ? <AiBusy feature="interviewPrep" compact label="Regenerating" /> : '✨ Regenerate'}
              </button>
            )}
            {/* T448 — he asked for this after the Autoflows interview, where
                the artefact that actually helped was a document he could
                read on the way there. I made that one by hand, outside the
                product, while this modal held the same material and could
                only put it on the clipboard. */}
            <button
              type="button"
              onClick={downloadPdf}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
              title="A4, one question per block — for reading away from the screen"
            >
              ⬇ Download PDF
            </button>
            <button
              type="button"
              onClick={copyAll}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              📋 Copy all
            </button>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              className="ml-auto rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-500 hover:border-red-700 hover:text-red-400"
            >
              Discard
            </button>
          </footer>
        )}
    </Modal>

      {confirmDiscard && (
        <ConfirmDialog
          title="Discard this interview prep pack?"
          message="Regenerating is cheap — but this set of questions will be replaced."
          confirmLabel="Discard"
          busyLabel="discarding…"
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={async () => {
            await discard();
            setConfirmDiscard(false);
          }}
        />
      )}
    </>
  );
}

function QuestionCard({ index, question }) {
  const tone = KIND_TONE[question.kind] ?? 'border-slate-700 text-slate-300 bg-slate-900/40';
  const kindLabel = KIND_LABEL[question.kind] ?? question.kind;
  return (
    <article className="rounded-lg border border-slate-800 bg-slate-950/30 p-3">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Q{index}</p>
          <p className="mt-0.5 text-sm font-medium text-slate-100">{question.question}</p>
        </div>
        <span className={'shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ' + tone}>
          {kindLabel}
        </span>
      </header>
      <dl className="mt-3 grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 text-xs">
        <dt className="text-[10px] uppercase tracking-wide text-slate-500">Situation</dt>
        <dd className="text-slate-300">{question.star.situation}</dd>
        <dt className="text-[10px] uppercase tracking-wide text-slate-500">Task</dt>
        <dd className="text-slate-300">{question.star.task}</dd>
        <dt className="text-[10px] uppercase tracking-wide text-slate-500">Action</dt>
        <dd className="text-slate-300">{question.star.action}</dd>
        <dt className="text-[10px] uppercase tracking-wide text-slate-500">Result</dt>
        <dd className="text-slate-300">{question.star.result}</dd>
      </dl>
      {question.tip && (
        <p className="mt-3 rounded border border-amber-700 bg-amber-950/15 px-2 py-1.5 text-[11px] text-amber-200">
          💡 {question.tip}
        </p>
      )}
    </article>
  );
}
